import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router';
import { paths } from 'src/routes/paths';
import { useHomeBandsStore } from 'src/store/personalization/home-bands/home-bands.store';
import * as Yup from 'yup';

export default function useHomeBandsDetails() {
  const { id } = useParams();
  const { pathname } = useLocation();
  const isEdit = pathname.includes('edit');
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const STATUS_OPTIONS = ['active', 'inactive'];
  const STATUS_LABEL = ['Ativo', 'Inativo'];

  const ARROW_LABEL = ['Sim', 'Não'];
  const ARROW_OPTIONS = [true, false];

  const INSTANT_TYPE_OPTIONS = ['nfScan', 'productRedeem', 'registerDate'];
  const INSTANT_TYPE_LABEL = ['Não escaneou NFs válidas', 'Não resgatou produtos', 'Data de cadastro VIVA'];

  const { homeBandToEdit, isLoadingCreate, isLoadingDetails, findOne , create, update, resetHomeBandDetails } =
    useHomeBandsStore();

  useEffect(() => {
    if (id) {
      findOne(id);
    } else {
      resetHomeBandDetails();
    }
  }, [id, isEdit]);

  const defaultValues = useMemo(
    () => ({
      text: homeBandToEdit?.text || '',
      icon: homeBandToEdit?.icon || '',
      urlToRedirect: homeBandToEdit?.urlToRedirect || '',
      startDate: homeBandToEdit?.startDate ? new Date(homeBandToEdit.startDate) : new Date(),
      endDate: homeBandToEdit?.endDate ? new Date(homeBandToEdit.endDate) : new Date(),
      status: homeBandToEdit?.status || 'active',
      priority: homeBandToEdit?.priority || 0,
      hasArrow: !!homeBandToEdit?.hasArrow,
      rules: homeBandToEdit?.rules?.map((r) => ({ ...r, startDate: new Date(r.startDate), endDate: new Date(r.endDate) })) || [],
    }),
    [isEdit, homeBandToEdit]
  );

  const homeBandSchema = Yup.object().shape({
    text: Yup.string().required('Campo obrigatório!').max(40, 'Texto deve ter no máximo 35 caracteres!'),
    icon: Yup.mixed().required('Campo obrigatório'),
    urlToRedirect: Yup.string().required('Campo obrigatório!'),
    startDate: Yup.date().required('Data de início é obrigatória'),
    endDate: Yup.date()
        .required('Data de término é obrigatória')
        .test(
        'is-minus-than-start',
        'Data de término deve ser maior que a data de início',
        (v, context) => {
            const { startDate } = context.parent;
            const start = new Date(startDate);
            const end = new Date(v);
            return end.getTime() >= start.getTime();
        }
    ),
    status: Yup.string().required('Campo obrigatório!'),
    priority: Yup.number().required('Campo obrigatório!'),
    hasArrow: Yup.boolean(),
    rules: Yup.array()
        .of(
            Yup.object().shape({
                ruleType: Yup.string().required('Campo obrigatório!'),
                startDate: Yup.date().required('Data de início é obrigatória'),
                endDate: Yup.date()
                    .required('Data de término é obrigatória')
                    .test(
                        'is-minus-than-start',
                        'Data de término deve ser maior que a data de início',
                        (v, context) => {
                          const { startDate } = context.parent;
                          const start = new Date(startDate);
                          const end = new Date(v);
                          return end.getTime() >= start.getTime();
                        }
                    ),
            })
        ),
  });

  const methods = useForm<any>({
    resolver: yupResolver(homeBandSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    reset,
    setValue,
    control,
    formState: { isSubmitting },
  } = methods;

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [homeBandToEdit, reset, defaultValues]);

  const onSubmit = handleSubmit(async (data: any) => {
    try {
      data.startDate = moment(data.startDate).startOf('day')
      data.endDate = moment(data.endDate).endOf('day')
      if (isEdit && id) {
        data.id = id
        await update(id, data);
        enqueueSnackbar('Faixa atualizada com sucesso!');
      } else {
        await create(data);
        enqueueSnackbar('Faixa criada com sucesso!');
      }
      navigate(paths.dashboard.homeBand);
    } catch (error) {
      console.error(error);
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'rules',
  });

  const handleDrop = useCallback(
    (acceptedFiles: any) => {
      const file = acceptedFiles[0];

      if (file) {
        setValue(
          'icon',
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          })
        );
      }
    },
    [setValue]
  );

  return {
    id,
    isLoadingCreate,
    isEdit,
    methods,
    fields,
    isSubmitting,
    control,
    isLoadingDetails,
    STATUS_OPTIONS,
    STATUS_LABEL,
    INSTANT_TYPE_OPTIONS,
    INSTANT_TYPE_LABEL,
    ARROW_LABEL,
    ARROW_OPTIONS,
    handleDrop,
    append,
    remove,
    onSubmit,
  };
}
